import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flag, ImagePlus, X, Loader2, CheckCircle2 } from "lucide-react";
import { uploadImage } from "@/components/post/ImagesPicker";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const REASONS = [["scam", "احتيال", "Scam"], ["wrong_category", "قسم خاطئ", "Wrong category"], ["prohibited", "منتج ممنوع", "Prohibited item"],
  ["duplicate", "إعلان مكرر", "Duplicate ad"], ["misleading", "معلومات مضللة", "Misleading info"], ["sold", "تم البيع", "Already sold"], ["other", "أخرى", "Other"]];

export function ReportAd({ listingId, className }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const T = (ar, en) => (lang === "en" ? en : ar);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [refNo, setRefNo] = useState(null);
  const addImages = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 3 - images.length);
    e.target.value = "";
    setUploading(true);
    try { for (const f of files) { const path = await uploadImage(f); setImages((x) => [...x, path].slice(0, 3)); } }
    catch { toast.error(T("فشل رفع الصورة", "Image upload failed")); }
    finally { setUploading(false); }
  };
  const openForm = () => { setReason(""); setNote(""); setImages([]); setRefNo(null); setOpen(true); };
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/listings/${listingId}/report`, { reason, note, images }); setRefNo(data.ref_no); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => (user ? openForm() : navigate("/login"))} className={className || "w-full h-12 rounded-2xl border border-red-200 text-red-600 font-semibold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors"} data-testid="detail-report-ad">
        <Flag className={className ? "w-4 h-4 shrink-0" : "w-5 h-5"} />{className ? T("إبلاغ", "Report") : T("الإبلاغ عن الإعلان", "Report this ad")}
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent dir={dir} className="max-w-sm" data-testid="report-ad-dialog">
          <DialogHeader><DialogTitle>{T("الإبلاغ عن الإعلان", "Report this ad")}</DialogTitle></DialogHeader>
          {refNo ? (
            <div className="text-center py-4 space-y-3" data-testid="report-ad-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <p className="font-bold" data-testid="report-ad-ref">{T("تم إرسال البلاغ برقم مرجعي:", "Report sent with reference number:")} <span dir="ltr">#{refNo}</span></p>
              <Button onClick={() => setOpen(false)} className="w-full rounded-xl" data-testid="report-ad-done">{T("حسناً", "OK")}</Button>
            </div>
          ) : <>
            <div className="grid grid-cols-2 gap-2">
              {REASONS.map(([id, ar, en]) => (
                <button key={id} type="button" onClick={() => setReason(id)} data-testid={`report-ad-reason-${id}`}
                  className={`h-11 rounded-xl border text-sm font-semibold transition-colors ${reason === id ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-200 hover:border-red-300"}`}>{T(ar, en)}</button>
              ))}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3} className="rounded-xl" placeholder={T("ملاحظة (اختياري)", "Note (optional)")} data-testid="report-ad-note" />
            <div data-testid="report-ad-images">
              <p className="text-sm font-semibold mb-2">{T("إرفاق صور كدليل (اختياري، حتى 3 صور)", "Attach evidence images (optional, up to 3)")}</p>
              <div className="flex gap-2">
                {images.map((p, i) => (
                  <div key={`${p}-${i}`} className="relative w-16 h-16 rounded-xl overflow-hidden border border-slate-200" data-testid={`report-ad-image-${i}`}>
                    <img src={imageUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setImages((x) => x.filter((_, j) => j !== i))} className="absolute top-0.5 end-0.5 bg-black/60 text-white rounded-full p-0.5" data-testid={`report-ad-image-remove-${i}`}><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {images.length < 3 && (
                  <label className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-300 hover:border-red-400 flex items-center justify-center cursor-pointer text-slate-500" data-testid="report-ad-image-add">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                    <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={addImages} data-testid="report-ad-image-input" />
                  </label>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button disabled={!reason || busy || uploading} onClick={submit} className="w-full rounded-xl bg-red-600 hover:bg-red-700 text-white" data-testid="report-ad-submit">{T("إرسال البلاغ", "Send report")}</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}
